/**
 * Table type selection step for custom request flow.
 * Multi-select card list for seating preferences (EventPage accessTypeCard pattern).
 * Skipped when restaurant has no table types.
 * @module Widget/CustomRequest/steps/TableTypeStep
 */

import React from 'react';
import type { CustomRequestTableOption } from '../../lib/types.ts';
import styles from '../CustomRequest.module.css';

interface TableTypeStepProps {
  tableTypes: CustomRequestTableOption[];
  selectedTypes: number[];
  onChange: (types: number[]) => void;
}

/**
 * Multi-select list of table types (Indoor, Outdoor, Bar, etc.).
 *
 * @param props - Component props
 * @returns Rendered table type selection
 */
const TableTypeStep: React.FC<TableTypeStepProps> = ({ tableTypes, selectedTypes, onChange }) => {
  const toggleType = (id: number) => {
    if (selectedTypes.includes(id)) {
      onChange(selectedTypes.filter((t) => t !== id));
    } else {
      onChange([...selectedTypes, id]);
    }
  };

  const selectAll = () => {
    onChange(tableTypes.map((t) => parseInt(t.id, 10)));
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <p className={styles.sectionSubtitle}>
          Select the seating types you're open to.
        </p>
        <button className={styles.selectAllLink} onClick={selectAll}>
          Select all
        </button>
      </div>

      <div className="flex flex-col gap-2">
        {tableTypes.map((type) => {
          const numId = parseInt(type.id, 10);
          const isSelected = selectedTypes.includes(numId);
          return (
            <button
              key={type.id}
              className={isSelected ? styles.selectableCardSelected : styles.selectableCard}
              onClick={() => toggleType(numId)}
            >
              <div className={isSelected ? styles.checkboxChecked : styles.checkbox}>
                {isSelected && (
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                )}
              </div>
              <span className={styles.selectableCardLabel}>{type.name}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default TableTypeStep;
